import { useState, useEffect, useContext } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { convertToJalali, formatGregorianDate } from "../../../utils/date";
import { LocaleContext } from "../../../routing/LangRouter";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { navigateToLoginPage } from "../../../utils/utility";
import { IS_MOBILE } from "../../../constants/breakpoints";

//import general components
import Skeleton from "../../../components/common/skeleton/skeleton";
import BaseTitle from "../../../components/common/base-title/base-title";

//import assets
import noImage from "../../../assets/imgs/no-image.png";

import "./my-itinerary.scss";

function MyItinerary() {
    //general
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const accessToken = localStorage.getItem("accessToken");
    const { t } = useTranslation();
    const navigate = useNavigate();

    //get itinerary list
    const [myItineraryList, setMyItineraryList] = useState([]);
    const [loading, setLoading] = useState(false);
    const getMyItineraryList = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/account/user/itinerary/list/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const list = response.data.results;
            setMyItineraryList(list);
            setLoading(false);
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
            }
            setLoading(false);
        }
    };
    useEffect(() => {
        getMyItineraryList();
    }, []);

    const viewItinerary = (planId, startDate, endDate, cityId) => {
        window.open(
            `/${locale}/view-itinerary?planId=${planId}&startDate=${startDate}&endDate=${endDate}&cityId=${cityId}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    return (
        <Grid className="my-itinerary" container item xs={12} rowGap={2} mt={{ xs: 5, md: 0 }} mb={5}>
            {!IS_MOBILE && <BaseTitle title={`${t("myItineraries")}`} dividerColor="1px solid var(--medium-gray-color)" />}
            {loading &&
                [...Array(10)].map((_, index) => (
                    <Grid className="my-itinerary__my-itinerary-item" key={index} container item xs={12} columnGap={3} alignItems="center">
                        <Grid container item xs={3} md="auto">
                            <Skeleton variant="rounded" animation="wave" width={80} height={80} />
                        </Grid>
                        <Grid container item xs={8} direction={{ xs: "row", md: "column" }}>
                            <Skeleton variant="text" animation="wave" width={200} height={20} />
                            <Skeleton variant="text" animation="wave" width={250} height={20} />
                        </Grid>
                    </Grid>
                ))}
            {myItineraryList?.length ? (
                myItineraryList?.map((myItineraryItem, index) => {
                    return (
                        <Grid
                            className="my-itinerary__my-itinerary-item"
                            key={index}
                            container
                            item
                            xs={12}
                            rowGap={1}
                            justifyContent="space-between"
                            alignItems="center"
                            onClick={() =>
                                viewItinerary(
                                    myItineraryItem?.plan,
                                    myItineraryItem?.start_datetime,
                                    myItineraryItem?.end_datetime,
                                    myItineraryItem?.city,
                                )
                            }
                        >
                            <Grid container item md={4} columnGap={1}>
                                <img className="my-itinerary__city-image" src={myItineraryItem?.city_obj?.image} alt="" />

                                <Grid className="my-itinerary__trip-details" container item xs="auto">
                                    <Grid container item xs={12} sx={{ fontWeight: "bold" }}>
                                        {["fa", "ar"].includes(locale) ? myItineraryItem?.city_obj?.name : myItineraryItem?.city_obj?.english_name}
                                    </Grid>
                                    <Grid container item xs={12} md={8}>
                                        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: "4px" }}>
                                           {`${t("from")}`} :  {locale === "fa" ?  convertToJalali(myItineraryItem?.start_datetime) : formatGregorianDate(myItineraryItem?.start_datetime, null, "YYYY/MM/DD")}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: "4px" }}>
                                            {`${t("to-2")}`} :  {locale === "fa" ?  convertToJalali(myItineraryItem?.end_datetime) : formatGregorianDate(myItineraryItem?.end_datetime, null, "YYYY/MM/DD")}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {!!myItineraryItem?.flight_number ? (
                                <>
                                    <Divider flexItem orientation="vertical" />
                                    <Divider width="100%" sx={{ display: { xs: "flex", md: "none" } }} />
                                    <Grid
                                        className="my-itinerary__tour"
                                        container
                                        item
                                        md={2}
                                        justifyContent={{ xs: "space-between", md: "center" }}
                                        alignItems={{ xs: "center", md: "flex-start" }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: "#D6FFFF",
                                                color: "#006363",
                                                padding: "4px 8px",
                                                borderRadius: "12px",
                                                display: "inline-block",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {`${t("departureFlight")}`}
                                        </Box>
                                        <Grid container item xs={6} md={12} alignItems="center">
                                            <img className="my-itinerary__flight-logo" src={myItineraryItem?.flight_airline_logo} />
                                            <div className="my-itinerary__flight-airline">
                                                {" "}
                                                {`${t("airline")}`} {myItineraryItem?.flight_airline}
                                            </div>
                                        </Grid>
                                        <Grid container item xs={6} md={12}>
                                            {`${t("flightNumber")}`}: {myItineraryItem?.flight_number}
                                        </Grid>
                                        <Grid container item xs={6} md={12}>
                                            {`${t("flightTime")}`}: {myItineraryItem?.flight_departure_time}
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                ""
                            )}

                            {!!myItineraryItem?.flight_number2 ? (
                                <>
                                    <Divider flexItem orientation="vertical" />
                                    <Divider width="100%" sx={{ display: { xs: "flex", md: "none" } }} />
                                    <Grid
                                        className="my-itinerary__tour"
                                        container
                                        item
                                        md={2}
                                        justifyContent={{ xs: "space-between", md: "center" }}
                                        alignItems={{ xs: "center", md: "flex-start" }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: "#D6FFFF",
                                                color: "#006363",
                                                padding: "4px 8px",
                                                borderRadius: "12px",
                                                display: "inline-block",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {`${t("returnFlight")}`}
                                        </Box>
                                        <Grid container item xs={6} md={12} alignItems="center">
                                            <img className="my-itinerary__flight-logo" src={myItineraryItem?.flight_airline_logo2} />

                                            <div className="my-itinerary__flight-airline">
                            
                                                {`${t("airline")}`} {myItineraryItem?.flight_airline2}
                                            </div>
                                        </Grid>
                                        <Grid container item xs={6} md={12}>
                                            {`${t("flightNumber")}`}: {myItineraryItem?.flight_number2}
                                        </Grid>
                                        <Grid container item xs={6} md={12}>
                                            {`${t("flightTime")}`}: {myItineraryItem?.flight_departure_time2}
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                ""
                            )}

                            {myItineraryItem?.accommodation ? (
                                <>
                                    <Divider flexItem orientation="vertical" />
                                    <Divider width="100%" sx={{ display: { xs: "flex", md: "none" } }} />

                                    <Grid className="my-itinerary__tour" container item md={2} justifyContent="center" alignItems="flex-start">
                                        <Box
                                            sx={{
                                                backgroundColor: "#D6FFFF",
                                                color: "#006363",
                                                padding: "4px 8px",
                                                borderRadius: "12px",
                                                display: "inline-block",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {`${t("hotel")}`}
                                        </Box>
                                        <Grid container item xs={12} spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <img
                                                    className="my-itinerary__city-image-second"
                                                    src={
                                                        myItineraryItem?.accommodation_obj?.image?.image.length
                                                            ? "https://api.atripa.com" + myItineraryItem?.accommodation_obj?.image?.image
                                                            : noImage
                                                    }
                                                    alt="Accommodation"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        borderRadius: "8px",
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <Typography variant="body1" sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                                                    {myItineraryItem?.accommodation_obj?.name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: "4px" }}>
                                                    {`${t("from")}`} :  {locale === "fa" ?  convertToJalali(myItineraryItem?.accommodation_check_in) : formatGregorianDate(myItineraryItem?.accommodation_check_in, null, "YYYY/MM/DD")}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: "4px" }}>
                                                    {`${t("to-2")}`} :  {locale === "fa" ?  convertToJalali(myItineraryItem?.accommodation_check_out) : formatGregorianDate(myItineraryItem?.accommodation_check_out, null, "YYYY/MM/DD")}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                ""
                            )}
                        </Grid>
                    );
                })
            ) : (
                <Grid container item xs={12} justifyContent="center">
                    {t("noItinerariesYet")}
                </Grid>
            )}
        </Grid>
    );
}

export default MyItinerary;
